import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import "./PopuliPage.css";

const API = {
  list: "https://populi-document-listining-7kpxqstxwq-ey.a.run.app",
  createFolder: "https://populi-new-document-7kpxqstxwq-ey.a.run.app",
  upload: "https://populi-document-upload-7kpxqstxwq-ey.a.run.app",
  process: "https://populi-document-upload-process-7kpxqstxwq-ey.a.run.app",
  remove: "https://populi-document-delete-7kpxqstxwq-ey.a.run.app",
  download: "https://populi-getfile-795950092325.europe-west3.run.app",
};

const DOCUMENT_TYPES = [
  ["prosedur", "Prosedür / Politika Dokümanı"],
  ["faq", "Sıkça Sorulan Sorular (SSS)"],
  ["iletisim", "İletişim Bilgileri"],
  ["egitim", "Eğitim Dokümanı"],
  ["operasyon", "Operasyon Dokümanı"],
  ["bt_dokuman", "IT Dokümanı"],
  ["other", "Diğer"],
];

function Icon({ name }) {
  const paths = {
    plus: <path d="M12 5v14M5 12h14" />,
    refresh: <path d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7" />,
    upload: <path d="M12 16V4m0 0L7 9m5-5 5 5M5 15v5h14v-5" />,
    download: <path d="M12 4v12m0 0-5-5m5 5 5-5M5 20h14" />,
    trash: <path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    folder: <path d="M3 7h7l2 2h9v10H3V7Z" />,
  };

  return (
    <svg className="populi-icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="populi-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="populi-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="populi-modal__header">
          <h2>{title}</h2>
          <button className="populi-icon-button" type="button" onClick={onClose} aria-label="Kapat">
            <Icon name="close" />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function appendFormData(values) {
  const data = new FormData();
  Object.entries(values).forEach(([key, value]) => data.append(key, value));
  return data;
}

async function postForm(url, values) {
  const response = await fetch(url, { method: "POST", body: appendFormData(values) });
  if (!response.ok) throw new Error(`İstek başarısız (${response.status})`);
  return response.json();
}

async function postFormWithoutResponse(url, values) {
  const response = await fetch(url, { method: "POST", body: appendFormData(values) });
  if (!response.ok) throw new Error(`İstek başarısız (${response.status})`);
}

export default function PopuliPage({ userEmail: emailProp, onUnauthorized }) {
  const [projects, setProjects] = useState([]);
  const [roles, setRoles] = useState([]);
  const [department, setDepartment] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);

  const userEmail =
    emailProp || (typeof window !== "undefined" ? sessionStorage.getItem("userEmail") : "") || "";

  const loadProjects = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    setError("");
    try {
      const data = await postForm(API.list, { useremail: userEmail });
      setProjects(Array.isArray(data?.result) ? data.result : []);
      setRoles(Array.isArray(data?.result_roles) ? data.result_roles : []);
      setDepartment(data?.department || "");
    } catch (requestError) {
      setError(requestError.message || "Dokümanlar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) {
      onUnauthorized?.();
      return;
    }
    loadProjects();
  }, [loadProjects, onUnauthorized, userEmail]);

  const groups = useMemo(() => {
    const grouped = projects.reduce((result, item) => {
      const key = item.projectID;
      if (!result[key]) result[key] = [];
      result[key].push(item);
      return result;
    }, {});

    return Object.values(grouped)
      .map((items) => ({
        items: [...items].sort(
          (a, b) => new Date(b.process_time || 0) - new Date(a.process_time || 0),
        ),
      }))
      .sort(
        (a, b) =>
          new Date(b.items[0]?.createDate || 0) - new Date(a.items[0]?.createDate || 0),
      )
      .filter(({ items }) => {
        const first = items[0] || {};
        const searchable = [
          first.projectname,
          first.projectCode,
          first.organisationDept,
          first.accessRole,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("tr");
        return searchable.includes(query.trim().toLocaleLowerCase("tr"));
      });
  }, [projects, query]);

  async function createFolder(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const projectName = String(form.get("projectName") || "").trim();
    if (!projectName) return;

    const selectedRole = roles.find((item) => item.role === form.get("dataCenter"));
    setLoading(true);
    setError("");
    try {
      await postForm(API.createFolder, {
        projectName,
        useremail: userEmail,
        organisationDept: form.get("organisationDept") || department,
        dataCenter: form.get("dataCenter") || "",
        botname: selectedRole?.botname || roles[0]?.botname || "",
      });
      setFolderModalOpen(false);
      await loadProjects();
    } catch (requestError) {
      setError(requestError.message || "Klasör oluşturulamadı.");
      setLoading(false);
    }
  }

  async function uploadDocument(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const file = form.get("file");
    const documentName = String(form.get("projectdocumentName") || "").trim();
    if (!documentName || !(file instanceof File) || !file.name) return;

    setLoading(true);
    setError("");
    try {
      const uploadResult = await postForm(API.upload, {
        projectID: uploadTarget.projectID,
        useremail: userEmail,
        projectdocumentName: documentName,
        projectDocument: form.get("projectDocument"),
        accessRole: uploadTarget.accessRole || "",
        fileExtension: file.name.split(".").pop() || "",
        file,
      });

      setUploadTarget(null);
      await postFormWithoutResponse(API.process, {
        projectID: uploadTarget.projectID,
        documentID: uploadResult.documentID,
      });
      await loadProjects();
    } catch (requestError) {
      setError(requestError.message || "Belge yüklenemedi.");
      setLoading(false);
    }
  }

  async function removeItem(projectID, documentID, type) {
    if (!window.confirm("Silme işlemini onaylıyor musunuz?")) return;
    setLoading(true);
    setError("");
    try {
      await postForm(API.remove, { projectID, documentID, type });
      await loadProjects();
    } catch (requestError) {
      setError(requestError.message || "Kayıt silinemedi.");
      setLoading(false);
    }
  }

  async function downloadDocument(projectID, documentID) {
    setLoading(true);
    setError("");
    try {
      const data = await postForm(API.download, { projectID, documentID });
      if (!data?.signed_url) throw new Error("İndirme bağlantısı alınamadı.");
      const link = document.createElement("a");
      link.href = data.signed_url;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (requestError) {
      setError(requestError.message || "Dosya indirilemedi.");
    } finally {
      setLoading(false);
    }
  }

  if (!userEmail) {
    return <div className="populi-page populi-empty">Bu sayfayı görüntülemek için giriş yapmalısınız.</div>;
  }

  return (
    <main className="populi-page">
      <section className="populi-card">
        <div className="populi-toolbar">
          <button className="populi-button populi-button--primary" onClick={() => setFolderModalOpen(true)}>
            <Icon name="plus" /> Yeni Klasör
          </button>
          <button className="populi-button populi-button--secondary" onClick={loadProjects} disabled={loading}>
            <Icon name="refresh" /> Yenile
          </button>
        </div>

        <div className="populi-search">
          <label htmlFor="populi-project-search">Ara</label>
          <input
            id="populi-project-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Klasör adı veya organizasyon ile ara..."
          />
        </div>

        {error && <div className="populi-alert" role="alert">{error}</div>}

        <div className="populi-table-wrap">
          <table className="populi-table">
            <thead>
              <tr>
                <th>Belge Türü</th>
                <th>Belge Adı</th>
                <th>Durum</th>
                <th>İndir</th>
                <th>Sil</th>
              </tr>
            </thead>
            <tbody>
              {!loading && groups.length === 0 && (
                <tr><td className="populi-table__empty" colSpan="5">Henüz klasör bulunmuyor.</td></tr>
              )}

              {groups.map(({ items }) => {
                const folder = items.find((item) => item.documentID == null) || items[0];
                const documents = items.filter((item) => item.documentID != null);
                return (
                  <Fragment key={folder.projectID}>
                    <tr className="populi-folder-row">
                      <td colSpan="5">
                        <div className="populi-folder-row__content">
                          <div className="populi-folder-meta">
                            <Icon name="folder" />
                            <strong>{folder.projectname || "Adsız klasör"}</strong>
                            <span>Organizasyon: {folder.organisationDept || "—"}</span>
                            <span>Erişim: {folder.accessRole || "—"}</span>
                          </div>
                          <div className="populi-folder-actions">
                            <button
                              className="populi-button populi-button--upload"
                              onClick={() => setUploadTarget(folder)}
                            >
                              <Icon name="upload" /> Belge Yükle
                            </button>
                            <button
                              className="populi-icon-button populi-icon-button--danger"
                              onClick={() => removeItem(folder.projectID, 1, 1)}
                              aria-label={`${folder.projectname || "Klasörü"} sil`}
                            >
                              <Icon name="trash" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>

                    {documents.length === 0 ? (
                      <tr><td className="populi-table__empty" colSpan="5">Belge eklenmemiş.</td></tr>
                    ) : documents.map((item) => (
                      <tr key={item.documentID}>
                        <td>{item.documentType || "—"}</td>
                        <td>{item.documentName || "—"}</td>
                        <td><span className="populi-status">{item.status || "—"}</span></td>
                        <td>
                          <button
                            className="populi-icon-button populi-icon-button--success"
                            onClick={() => downloadDocument(item.projectID, item.documentID)}
                            aria-label={`${item.documentName || "Belgeyi"} indir`}
                          >
                            <Icon name="download" />
                          </button>
                        </td>
                        <td>
                          <button
                            className="populi-icon-button populi-icon-button--danger"
                            onClick={() => removeItem(item.projectID, item.documentID, 2)}
                            aria-label={`${item.documentName || "Belgeyi"} sil`}
                          >
                            <Icon name="trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {folderModalOpen && (
        <Modal title="Yeni Klasör" onClose={() => setFolderModalOpen(false)}>
          <form className="populi-form" onSubmit={createFolder}>
            <label>Klasör Adı<input name="projectName" required autoFocus /></label>
            <label>
              Doküman Erişimi
              <select name="dataCenter" required defaultValue={roles[0]?.role || ""}>
                {roles.map((item) => <option key={item.role} value={item.role}>{item.role}</option>)}
              </select>
            </label>
            <label>
              Organizasyon
              <select name="organisationDept" defaultValue={department}>
                <option value={department}>{department || "Organizasyon yok"}</option>
              </select>
            </label>
            <div className="populi-form__actions">
              <button type="button" className="populi-button populi-button--ghost" onClick={() => setFolderModalOpen(false)}>İptal</button>
              <button type="submit" className="populi-button populi-button--primary">Oluştur</button>
            </div>
          </form>
        </Modal>
      )}

      {uploadTarget && (
        <Modal title="Belge Yükle" onClose={() => setUploadTarget(null)}>
          <form className="populi-form" onSubmit={uploadDocument}>
            <label>Belge Adı<input name="projectdocumentName" required autoFocus /></label>
            <label>
              Belge Türü
              <select name="projectDocument" defaultValue={DOCUMENT_TYPES[0][0]}>
                {DOCUMENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label>Dosya<input name="file" type="file" accept=".csv,.txt,.docx,.pdf" required /></label>
            <div className="populi-form__actions">
              <button type="button" className="populi-button populi-button--ghost" onClick={() => setUploadTarget(null)}>İptal</button>
              <button type="submit" className="populi-button populi-button--primary">Yükle</button>
            </div>
          </form>
        </Modal>
      )}

      {loading && <div className="populi-loading" role="status"><span /> İşlem yapılıyor…</div>}
    </main>
  );
}
